import React from 'react';
import { Alert, AlertIcon, Box, Divider, Stack, Text } from '@chakra-ui/react';
import { IBaixaDados } from '../../../../hooks/useEstatuto';
import { formatDateBR } from '../utils';
import { formatarCpf } from '../../../estatutoPublico/PessoasField';

/** Dados da Baixa no card (21/10): alertas da spec (§7.2) e as 2 partes do formulário. */

const Linha: React.FC<{ rotulo: string; valor: React.ReactNode }> = ({ rotulo, valor }) => (
  <Box>
    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
      {rotulo}
    </Text>
    <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">
      {valor === null || valor === undefined || valor === '' ? '—' : valor}
    </Text>
  </Box>
);

const Titulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="xs" fontWeight="700" color="gray.500" mb={3}>
    {children}
  </Text>
);

const votos = (d: Record<string, any>, prefixo: string) =>
  `${d[`${prefixo}_favor`]} a favor, ${d[`${prefixo}_contra`]} contra, ${d[`${prefixo}_abstencao`]} abstenção(ões)`;

const cnpj = (d: string) => d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
const reais = (v: string | number) =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pessoa = (d: Record<string, any>, prefixo: string) =>
  `${d[`${prefixo}_nome`]} · CPF ${formatarCpf(d[`${prefixo}_cpf`] ?? '')} · ${d[`${prefixo}_email`]}`;

export const EstatutoBaixaDados: React.FC<{ dados: IBaixaDados }> = ({ dados }) => {
  const { convocacao: c, assembleia: a, alertas = [] } = dados;
  if (!c) return null;
  const nomePorCpf = Object.fromEntries((a?.presentes ?? []).map(p => [p.cpf, p.nome]));
  const local =
    c.formato === 'virtual'
      ? `Virtual · ${c.plataforma}`
      : `${c.local}${c.formato === 'hibrida' ? ` (híbrida · ${c.plataforma})` : ''}`;

  return (
    <>
      <Divider />
      {alertas.length > 0 && (
        <Alert status="warning" borderRadius="md" alignItems="flex-start" fontSize="xs">
          <AlertIcon />
          <Stack spacing={1}>
            {alertas.map(t => (
              <Text key={t}>{t}</Text>
            ))}
          </Stack>
        </Alert>
      )}
      <Box>
        <Titulo>BAIXA · 1ª PARTE (CONVOCAÇÃO)</Titulo>
        <Stack spacing={3}>
          <Linha rotulo="Nome no CNPJ" valor={c.nome_cnpj} />
          <Linha
            rotulo="CNPJ · registro no cartório"
            valor={`${cnpj(c.cnpj)} · ${c.registro_numero}`}
          />
          <Linha
            rotulo="Turma"
            valor={`${c.numero_turma} · ${c.nome_curso} · ${c.nome_instituicao}`}
          />
          <Linha rotulo="Último evento de formatura" valor={formatDateBR(c.data_ultimo_evento)} />
          <Linha
            rotulo="Assembleia convocada"
            valor={`${formatDateBR(c.data_assembleia)} às ${c.hora_1a} · 2ª convocação ${
              c.intervalo_2a_horas
            } h depois\n${local}`}
          />
          <Linha
            rotulo="Edital"
            valor={`${formatDateBR(c.data_edital)} · ${c.cidade_assinatura}`}
          />
          <Linha
            rotulo="Quem convoca"
            valor={`${c.convocante_nome} (${c.convocante_cargo}) · ${c.convocante_email}`}
          />
        </Stack>
      </Box>
      {a && (
        <Box>
          <Titulo>BAIXA · 2ª PARTE (ASSEMBLEIA)</Titulo>
          <Stack spacing={3}>
            <Linha
              rotulo="Assembleia"
              valor={`${formatDateBR(a.data_assembleia)} às ${a.hora_inicio} · ${a.convocacao} convocação · ${a.total_adimplentes} adimplentes`}
            />
            <Linha
              rotulo={`Presentes (${a.presentes.length})`}
              valor={a.presentes.map((p, i) => `${i + 1}. ${p.nome} · ${p.email}`).join('\n')}
            />
            <Linha
              rotulo="Mesa"
              valor={`Presidente: ${nomePorCpf[a.presidente_mesa_cpf]} · Secretário(a): ${
                nomePorCpf[a.secretario_mesa_cpf]
              }`}
            />
            <Linha rotulo="Dissolução" valor={votos(a, 'votos_dissolucao')} />
            <Linha
              rotulo="Contas finais"
              valor={`${a.tesoureiro_nome} · ${votos(a, 'votos_contas')}`}
            />
            <Linha
              rotulo="Patrimônio"
              valor={`Saldo R$ ${reais(a.saldo)} · fornecedores pagos: ${
                a.fornecedores_quitados ? 'sim' : 'não'
              }${a.data_encerramento_conta ? ` · conta encerrada em ${formatDateBR(a.data_encerramento_conta)}` : ''}`}
            />
            <Linha rotulo="Fiel depositário" valor={pessoa(a, 'fiel')} />
            <Linha rotulo="Presidente (liquidante)" valor={pessoa(a, 'presidente')} />
            <Linha rotulo="Requerimento" valor={formatDateBR(a.data_requerimento)} />
          </Stack>
        </Box>
      )}
    </>
  );
};
